import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { isPast } from '@marmon/frontend/common';
import {
	AlgoliaAnalysis,
	AnalysisStatus,
	AnalysisUploadProgress,
} from '@marmon/shared/types/analysis';
import { percentageToValue } from '@marmon/shared/types/common';
import { TuiIcon, TuiLoader } from '@taiga-ui/core';
import { Timestamp } from 'firebase/firestore';

type StatusData = {
	icon?: string;
	loader?: boolean;
	label: string;
	color: string;
};

const AnalysisStatusMap: { [key in AnalysisStatus]: StatusData } = {
	deleted: {
		icon: 'circle-x',
		label: 'analyses.dashboard.status.deleted',
		color: 'text-red-500',
	},
	error: {
		icon: 'circle-alert',
		label: 'analyses.dashboard.status.error',
		color: 'text-red-500',
	},
	expired: {
		icon: 'circle-x',
		label: 'analyses.dashboard.status.expired',
		color: 'text-red-500',
	},
	ready: {
		icon: 'circle-check',
		label: 'analyses.dashboard.status.ready',
		color: 'text-green-500',
	},
	uploading: {
		loader: true,
		label: 'analyses.dashboard.status.uploading',
		color: 'text-blue-500',
	},
};

@Component({
	standalone: true,
	selector: 'mar-analysis-status',
	imports: [TuiIcon, TranslocoPipe, TuiLoader],
	template: `
		@if (data(); as data) {
			@if (data.loader) {
				<tui-loader size="xs" class="h-3.5 w-3.5 shrink-0" />
			}

			@if (data.icon) {
				<tui-icon
					[class]="data.color + ' h-3.5 w-3.5'"
					[icon]="data.icon"
				/>
			}

			@if (percentage(); as percent) {
				{{ data.label + '-%' | transloco: { percent } }}
			} @else {
				{{ data.label | transloco }}
			}
		}
	`,
	styles: `
		:host tui-loader,
		:host tui-loader._loading {
			min-inline-size: 0.875rem;
			overflow: visible;
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'flex items-center gap-1' },
})
export class AnalysisStatusComponent {
	readonly analysis = input<AlgoliaAnalysis>();
	readonly uploadProgress = input<AnalysisUploadProgress>();

	readonly data = computed(() => {
		const analysis = this.analysis() as AlgoliaAnalysis;
		const date = new Date(analysis.expiresAt);
		const expired = isPast(Timestamp.fromDate(date));
		const status = expired ? 'expired' : analysis.status;
		return AnalysisStatusMap[status];
	});

	readonly percentage = computed(() => {
		const progress = this.uploadProgress();
		if (!progress) return null;
		return percentageToValue(progress.current / progress.total, 0);
	});
}
